import { useParams, Link } from "react-router-dom";
import { ArrowRight, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang, route, servicePath, postPath } from "../i18n";
import { useCompany, useSiteServices, useSitePosts } from "../components/SiteContext";
import SiteImage from "../components/SiteImage";
import { CATEGORIES } from "../data/posts";
import PageHero from "../components/PageHero";
import { SectionHead } from "../components/SectionHead";
import { Reveal } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import FinalCta from "../components/FinalCta";
import NotFound from "./NotFound";

const SERVICE_CATEGORY = {
  "tax-accounting": "taxes",
  "asset-protection": "protection",
  "estate-planning": "estate",
  "strategic-investments": "investments",
  immigration: "immigration",
  "business-consulting": "business",
};

const ListBlock = ({ title, items, tone = "light", testid }) => {
  const ink = tone === "dark" ? "text-[#F4F0E8]" : "text-[#171717]";
  const sub = tone === "dark" ? "text-[#F4F0E8]/65" : "text-[#171717]/70";
  return (
    <div data-testid={testid}>
      <h3 className={`eyebrow ${tone === "dark" ? "text-[#C6A46A]" : "text-[#8a6f3e]"}`}>{title}</h3>
      <ul className="mt-6 space-y-3">
        {items.map((item, i) => (
          <li key={i} className={`flex items-start gap-3 text-[0.95rem] leading-relaxed ${sub}`}>
            <span className="mt-2.5 h-px w-4 shrink-0 bg-[#C6A46A]" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const LABELS = {
  en: {
    whoFor: "Who this service is for",
    challenges: "Common challenges",
    evaluates: "What Aviah evaluates",
    support: "Potential areas of support",
    process: "The Aviah process",
    faqs: "Questions worth asking",
    related: "Related reading",
    next: "Next service",
  },
  es: {
    whoFor: "Para quién es este servicio",
    challenges: "Desafíos comunes",
    evaluates: "Lo que Aviah evalúa",
    support: "Posibles áreas de apoyo",
    process: "El proceso Aviah",
    faqs: "Preguntas que vale la pena hacer",
    related: "Lectura relacionada",
    next: "Siguiente servicio",
  },
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const company = useCompany();
  const services = useSiteServices();
  const allPosts = useSitePosts();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  const s = service[lang];
  const L = LABELS[lang];
  const related = allPosts.filter((p) => p.category === SERVICE_CATEGORY[slug]).slice(0, 2);
  const idx = services.findIndex((x) => x.slug === slug);
  const next = services[(idx + 1) % services.length];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: s.title,
        provider: { "@type": "Organization", name: company.name, telephone: company.phone },
        areaServed: "Orlando, Florida",
        description: s.short,
      },
      {
        "@type": "FAQPage",
        mainEntity: s.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <>
      <Seo title={s.seoTitle} siteName={company.name} description={s.seoDesc} jsonLd={jsonLd} />
      <PageHero
        eyebrow={`${t.nav.services} — ${service.num}`}
        lines={[s.title]}
        copy={s.heroSub}
        breadcrumb={[
          { label: t.nav.home, to: route(lang, "home") },
          { label: t.nav.services, to: route(lang, "services") },
          { label: s.title },
        ]}
        testid={`service-hero-${slug}`}
      />

      <section className="bg-[#F4F0E8]">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid gap-16 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <Reveal>
                <span className="text-5xl font-light text-[#C6A46A]">{service.num}</span>
                <div className="mt-8 space-y-5">
                  {s.overview.map((p, i) => (
                    <p key={i} className="text-[0.95rem] leading-relaxed text-[#171717]/75 sm:text-base">
                      {p}
                    </p>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="relative mt-12">
                  <div className="absolute -left-3 -top-3 h-full w-full border border-[#C6A46A]" aria-hidden="true" />
                  <SiteImage
                    slot={service.img}
                    className="relative"
                    imgClassName="aspect-[16/8] w-full object-cover grayscale-[30%]"
                  />
                </div>
              </Reveal>
            </div>
            <div className="space-y-12">
              <Reveal delay={0.1}>
                <ListBlock title={L.whoFor} items={s.whoFor} testid="service-who" />
              </Reveal>
              <Reveal delay={0.15}>
                <ListBlock title={L.challenges} items={s.challenges} testid="service-challenges" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#EEE9DF]">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid gap-16 lg:grid-cols-2">
            <Reveal>
              <ListBlock title={L.evaluates} items={s.evaluates} testid="service-evaluates" />
            </Reveal>
            <Reveal delay={0.12}>
              <ListBlock title={L.support} items={s.support} testid="service-support" />
            </Reveal>
          </div>

          <div className="mt-20">
            <SectionHead eyebrow={L.process} title={t.home.method.title} tone="light" />
            <div className="mt-12 grid gap-8 md:grid-cols-4">
              {t.home.method.steps.map((step, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="border-t border-[#171717]/15 pt-6">
                    <span className="text-3xl font-light text-[#C6A46A]">{step.n}</span>
                    <h4 className="mt-3 text-sm font-normal uppercase tracking-[0.14em] text-[#171717]">{step.t}</h4>
                    <p className="mt-3 text-xs leading-relaxed text-[#171717]/60">{step.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F4F0E8]">
        <div className="mx-auto max-w-4xl px-5 py-20 sm:px-8 sm:py-28">
          <SectionHead eyebrow={L.faqs} tone="light" align="center" />
          <div className="mt-10">
            <FaqAccordion items={s.faqs} tone="light" testid="service-faq" />
          </div>
          <Reveal delay={0.1}>
            <div className="mt-12 flex items-start gap-4 border border-[#C6A46A]/40 bg-[#EEE9DF] p-6" data-testid="service-disclaimer">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#8a6f3e]" strokeWidth={1.5} />
              <p className="text-xs leading-relaxed text-[#171717]/70">
                <span className="font-medium uppercase tracking-[0.14em]">{t.common.disclaimerTitle}: </span>
                {s.disclaimer}
              </p>
            </div>
          </Reveal>

          {related.length > 0 && (
            <div className="mt-16">
              <h3 className="eyebrow text-[#8a6f3e]">{L.related}</h3>
              <div className="mt-6 space-y-4">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={postPath(lang, p.slug)}
                    className="group flex items-center justify-between gap-4 border border-[#171717]/12 p-5 transition-colors hover:border-[#C6A46A]"
                  >
                    <div>
                      <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[#8a6f3e]">
                        {CATEGORIES[p.category]?.[lang] || p.category}
                      </span>
                      <p className="mt-1 text-sm font-normal text-[#171717]">{p[lang]?.title}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-[#C6A46A] transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-16 border-t border-[#171717]/12 pt-10">
            <Link
              to={servicePath(lang, next.slug)}
              data-testid="service-next"
              className="group flex items-center justify-between"
            >
              <div>
                <span className="eyebrow text-[#8a6f3e]">{L.next}</span>
                <p className="mt-2 text-2xl font-light tracking-tight text-[#171717]">
                  <span className="mr-3 text-[#C6A46A]">{next.num}</span>
                  {next[lang].title}
                </p>
              </div>
              <ArrowRight className="h-6 w-6 text-[#C6A46A] transition-transform group-hover:translate-x-2" />
            </Link>
          </div>
        </div>
      </section>

      <FinalCta />
    </>
  );
};

export default ServiceDetail;
